/**
 * Iter 73 Phase 6 — Elite Layers Panel.
 *
 * Operational layer control for the Sheet Viewer. Reads REAL data
 * from the existing Iter 73 collections (markups, project_objects,
 * measurements, pins, photos, documents, sheets). NEVER fakes counts.
 *
 * Layers shipped (Session 2):
 *   - Base Drawing        (always visible reference, not toggleable)
 *   - Markups             (toggle, count from markupState)
 *   - Measurements        (toggle, count from API)
 *   - Objects             (toggle, count from /projects/{p}/objects/summary)
 *   - Utilities           (subset of Objects with type=utility, with sub-legend)
 *   - Pins                (toggle, count from pins prop)
 *   - Photos              (toggle, count from /sheets/{s}/photos)
 *   - Documents           (toggle, project-level docs count)
 *   - Revisions           (read-only count from /sheets/{s}/revisions)
 *
 * State persistence: per-tenant + per-sheet localStorage.
 * Controls: per-row visibility, isolate, show all, hide all, reset.
 *
 * NOTE: this panel is the *control surface*; on-canvas rendering for
 * each layer is wired into the existing overlay components in
 * SheetViewer (UtilityIntelLayer, LayerOverlay, etc.). The toggle
 * state is propagated via the `forgedops:layer-visibility` window
 * event so existing layer renderers can subscribe without refactor.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff, Focus, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { projectObjectsSummary, listProjectObjects } from "@/lib/markupsApi";

const STORAGE_KEY = (tenant, sheet) => `fop_layers_v1_${tenant}_${sheet}`;

const DEFAULT_LAYERS = {
  base: { id: "base",        label: "Base Drawing", visible: true, locked: true },
  markups: { id: "markups",  label: "Markups",      visible: true },
  measurements: { id: "measurements", label: "Measurements", visible: true },
  objects: { id: "objects",  label: "Objects",      visible: true },
  utilities: { id: "utilities", label: "Utilities", visible: true },
  pins: { id: "pins",        label: "Pins",         visible: true },
  photos: { id: "photos",    label: "Photos",       visible: true },
  documents: { id: "documents", label: "Documents", visible: true },
  revisions: { id: "revisions", label: "Revisions", visible: true },
};

const UTILITY_COLORS = {
  water: "#2563eb",          // deeper blue — AA on white ~5.2:1
  sewer: "#854d0e",          // deeper brown-amber — AA on white ~6.0:1
  storm: "#0e7490",          // deeper teal — AA on white ~5.6:1 (was #10b981)
  electric: "#b91c1c",       // darker red — keeps electric distinct from brand orange
  gas: "#a16207",            // darker amber — AA on white ~5.3:1 (was #fbbf24 which was ~2:1)
  communications: "#7c3aed", // deeper violet — AA on white ~6.2:1
  fiber: "#be185d",          // deeper pink — AA on white ~5.5:1
  telecom: "#9333ea",        // distinct purple — AA on white ~5.4:1
  unknown: "#525252",        // high-contrast gray — AA on white ~7.8:1 (was #9ca3af)
};

function getStored(tenant, sheet) {
  if (typeof window === "undefined" || !tenant || !sheet) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY(tenant, sheet));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function setStored(tenant, sheet, state) {
  if (typeof window === "undefined" || !tenant || !sheet) return;
  try { window.localStorage.setItem(STORAGE_KEY(tenant, sheet), JSON.stringify(state)); }
  catch {}
}

function broadcast(visibility) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("forgedops:layer-visibility", { detail: visibility }));
}

export default function LayersPanel({ projectId, sheetId, tenantId, pinsCount = 0, markupsCount = 0 }) {
  const [layers, setLayers] = useState(() => {
    const stored = getStored(tenantId, sheetId);
    return stored?.layers || DEFAULT_LAYERS;
  });
  const [counts, setCounts] = useState({
    measurements: null, objects: null, utilities: null,
    photos: null, documents: null, revisions: null,
  });
  const [utilityKindCounts, setUtilityKindCounts] = useState({});

  useEffect(() => {
    if (!projectId || !sheetId) return;
    let alive = true;
    Promise.all([
      api.get(`/sheets/${sheetId}/measurements`).then((r) => (r.data || []).length).catch(() => null),
      projectObjectsSummary(projectId).then((s) => s),
      listProjectObjects(projectId, { type: "utility" }).then((rows) => rows),
      api.get(`/sheets/${sheetId}/photos`).then((r) => (r.data || []).length).catch(() => null),
      api.get(`/projects/${projectId}/documents`).then((r) => (r.data?.documents || []).length).catch(() => null),
      api.get(`/sheets/${sheetId}/revisions`).then((r) => (r.data || []).length).catch(() => null),
    ]).then(([measurements, objSummary, utilRows, photos, documents, revisions]) => {
      if (!alive) return;
      // B2-10 (independent recheck batch 2, 2026-10-01) — reopens
      // R-06. A denied/failed objects read used to render as "0
      // items", indistinguishable from a verified-empty project. Show
      // "—" (unknown, not a trusted zero) instead, same treatment the
      // null/`—` branch below already gives a request that errored
      // outright.
      setCounts({
        measurements,
        objects: objSummary?._forbidden || objSummary?._degraded ? null : (objSummary?.total ?? null),
        utilities: utilRows?._forbidden || utilRows?._degraded ? null : (utilRows?.length ?? null),
        photos, documents, revisions,
      });
      const kinds = {};
      (utilRows?._forbidden || utilRows?._degraded ? [] : (utilRows || [])).forEach((u) => {
        const k = u.metadata?.utility_kind || "unknown";
        kinds[k] = (kinds[k] || 0) + 1;
      });
      setUtilityKindCounts(kinds);
    });
    return () => { alive = false; };
  }, [projectId, sheetId, markupsCount, pinsCount]);

  // Broadcast current state on every change so existing overlay renderers
  // can react without owning state.
  useEffect(() => {
    const visibility = Object.fromEntries(Object.values(layers).map((l) => [l.id, l.visible]));
    broadcast(visibility);
    if (tenantId && sheetId) setStored(tenantId, sheetId, { layers });
  }, [layers, tenantId, sheetId]);

  const toggle = (id) => setLayers((prev) => {
    const l = prev[id];
    if (!l || l.locked) return prev;
    return { ...prev, [id]: { ...l, visible: !l.visible } };
  });
  const isolate = (id) => setLayers((prev) => {
    const next = {};
    for (const k of Object.keys(prev)) {
      next[k] = { ...prev[k], visible: prev[k].locked || k === id };
    }
    return next;
  });
  const showAll = () => setLayers((prev) => {
    const next = {};
    for (const k of Object.keys(prev)) next[k] = { ...prev[k], visible: true };
    return next;
  });
  const hideAll = () => setLayers((prev) => {
    const next = {};
    for (const k of Object.keys(prev)) next[k] = { ...prev[k], visible: prev[k].locked };
    return next;
  });
  const reset = () => setLayers(DEFAULT_LAYERS);

  const countFor = (id) => {
    if (id === "markups") return markupsCount;
    if (id === "pins") return pinsCount;
    if (id === "measurements") return counts.measurements;
    if (id === "objects") return counts.objects;
    if (id === "utilities") return counts.utilities;
    if (id === "photos") return counts.photos;
    if (id === "documents") return counts.documents;
    if (id === "revisions") return counts.revisions;
    return null;
  };

  return (
    <div data-testid="layers-panel">
      <div className="px-3 py-2 border-b border-[var(--fop-border)] flex items-center justify-between bg-[var(--fop-surface-2)]">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">Layers</div>
        <div className="flex gap-1">
          <ToolBtn onClick={showAll} title="Show all" testid="layers-show-all">All</ToolBtn>
          <ToolBtn onClick={hideAll} title="Hide all" testid="layers-hide-all">None</ToolBtn>
          <ToolBtn onClick={reset} title="Reset" testid="layers-reset"><RotateCcw size={11} /></ToolBtn>
        </div>
      </div>

      <ul data-testid="layers-list">
        {Object.values(layers).map((l) => {
          const count = countFor(l.id);
          return (
            <li key={l.id}
                className={`px-3 py-2 border-b border-[var(--fop-border)] flex items-center gap-2 ${l.locked ? "opacity-70" : ""}`}
                data-testid={`layer-row-${l.id}`}>
              <button
                onClick={() => toggle(l.id)}
                disabled={l.locked}
                title={l.locked ? "Base drawing — always visible" : (l.visible ? "Hide layer" : "Show layer")}
                data-testid={`layer-toggle-${l.id}`}
                className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors
                  ${l.visible ? "text-[var(--fop-primary)]" : "text-[var(--fop-text-3)]"}
                  ${l.locked ? "cursor-not-allowed" : "hover:bg-[var(--fop-surface-2)]"}`}
              >
                {l.visible ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-[var(--fop-text)] font-medium">{l.label}</div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                  {count == null ? "—" : `${count} ${count === 1 ? "item" : "items"}`}
                </div>
              </div>
              {!l.locked && (
                <button
                  onClick={() => isolate(l.id)}
                  title={`Isolate ${l.label}`}
                  data-testid={`layer-isolate-${l.id}`}
                  className="w-9 h-9 flex items-center justify-center rounded-md text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)]"
                ><Focus size={14} /></button>
              )}
            </li>
          );
        })}
      </ul>

      {/* Utility sub-legend — surfaced only when utilities are present */}
      {layers.utilities.visible && (counts.utilities ?? 0) > 0 && (
        <div className="px-3 py-3 border-t border-[var(--fop-border)] bg-[var(--fop-surface-2)]" data-testid="utility-legend">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-2">Utility Legend</div>
          <div className="grid grid-cols-2 gap-1.5">
            {Object.entries(utilityKindCounts).map(([kind, n]) => (
              <div key={kind} className="flex items-center gap-2" data-testid={`utility-legend-${kind}`}>
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: UTILITY_COLORS[kind] || UTILITY_COLORS.unknown }} />
                <span className="text-[11px] text-[var(--fop-text)] capitalize flex-1 truncate">{kind}</span>
                <span className="text-[10px] font-mono text-[var(--fop-text-3)]">{n}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ToolBtn({ onClick, children, title, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="text-[9px] font-mono uppercase tracking-wider px-2 py-1 rounded border border-[var(--fop-border)] text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface)]"
    >
      {children}
    </button>
  );
}

export { UTILITY_COLORS };
